import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { NonNullableFormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { InputDialogConfig, DialogResult } from '../../../interfaces/dialog.interface';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-input-dialog',
  templateUrl: './input-dialog.component.html',
  styleUrl: './input-dialog.component.scss',
  standalone: true,
  imports: [ReactiveFormsModule, MatDialogModule, MatFormFieldModule, MatInputModule, MatButtonModule, MatIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InputDialogComponent implements OnInit {
  data = inject<InputDialogConfig>(MAT_DIALOG_DATA);

  inputForm: FormGroup;

  public dialogRef = inject(MatDialogRef<InputDialogComponent>);
  private fb = inject(NonNullableFormBuilder);

  constructor() {
    this.inputForm = this.fb.group({
      inputValue: ['']
    });
  }

  ngOnInit(): void {
    // Configuración por defecto
    if (!this.data.confirmText) {
      this.data.confirmText = 'Aceptar';
    }
    if (!this.data.cancelText) {
      this.data.cancelText = 'Cancelar';
    }
    if (!this.data.icon) {
      this.data.icon = 'input';
    }
    if (!this.data.inputType) {
      this.data.inputType = 'text';
    }

    // Configurar validadores
    const validators = [];
    if (this.data.required) {
      validators.push(Validators.required);
    }
    if (this.data.minLength) {
      validators.push(Validators.minLength(this.data.minLength));
    }
    if (this.data.maxLength) {
      validators.push(Validators.maxLength(this.data.maxLength));
    }
    if (this.data.pattern) {
      validators.push(Validators.pattern(this.data.pattern));
    }
    if (this.data.inputType === 'email') {
      validators.push(Validators.email);
    }

    // Actualizar el control con validadores y valor inicial
    this.inputForm.get('inputValue')?.setValidators(validators);
    this.inputForm.get('inputValue')?.setValue(this.data.inputValue || '');
    this.inputForm.get('inputValue')?.updateValueAndValidity();
  }

  onConfirm(): void {
    if (this.inputForm.valid) {
      const result: DialogResult = {
        confirmed: true,
        value: this.inputForm.get('inputValue')?.value
      };
      this.dialogRef.close(result);
    }
  }

  onCancel(): void {
    const result: DialogResult = {
      confirmed: false
    };
    this.dialogRef.close(result);
  }

  get inputControl() {
    return this.inputForm.get('inputValue');
  }

  hasFieldError(): boolean {
    const control = this.inputControl;
    return !!(control && control.invalid && control.touched);
  }

  getFieldError(): string {
    const control = this.inputControl;
    if (control && control.errors && control.touched) {
      if (control.errors['required']) return `${this.data.inputLabel} es requerido`;
      if (control.errors['email']) return 'Formato de email inválido';
      if (control.errors['minlength']) return `Mínimo ${control.errors['minlength'].requiredLength} caracteres`;
      if (control.errors['maxlength']) return `Máximo ${control.errors['maxlength'].requiredLength} caracteres`;
      if (control.errors['pattern']) return 'Formato inválido';
    }
    return '';
  }
}